import { useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { BodySmall, BodyTinyMedium } from '../../components/styles/Typography'
import { theme } from '../../theme/theme'
import { ComponentLayout } from '../../components/layouts/ComponentLayout'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { useIsFeatureEnabled, useIsFeatureFlagsLoaded } from '../../state/selectors'
import { FeatureFlagId } from '../../api/ester-b2b-api/private/featureFlags/types'
import {
  useGetHealthProgramStatistics,
  useGetScreeningInsights,
  useGetTestimonials,
} from '../../api/ester-b2b-api/private/analytics/analyticsQueries'
import { HealthProgramSection } from '../../components/admin/statistics/sections/HealthProgramSection'
import { EducationalSection } from '../../components/admin/statistics/sections/EducationalSection'
import { SummarySection } from '../../components/admin/statistics/sections/SummarySection'
import { TestimonialsSection } from '../../components/admin/statistics/sections/TestimonialsSection'
import { OrganisationNeedsSection } from '../../components/admin/statistics/sections/OrganisationNeedsSection'
import { ExportReportButton } from '../../components/admin/statistics/print/ExportReportButton'
import { PrintReport } from '../../components/admin/statistics/print/PrintReport'
import { getSummaryMetrics } from '../../utils/statistics/summary'
import { hasInsufficientReportData } from '../../utils/statistics/reportData'
import { LegacyStatistics } from './LegacyStatistics'
import { CompetenceSection } from '../../components/admin/statistics/sections/CompetenceSection'

export const Statistics = () => {
  const isFeatureFlagsLoaded = useIsFeatureFlagsLoaded()
  const isNewStatisticsEnabled = useIsFeatureEnabled(FeatureFlagId.USE_NEW_STATISTICS)

  // AuthenticationGuard fills the flag store in a useEffect, so on the first commit it is still
  // empty and the legacy view would mount and fire its queries. Same guard as FeatureFlagGuard.
  if (!isFeatureFlagsLoaded) return <LoadingSpinner color={theme.color.plum} size={60} />

  // Two components, not one branching render, so only the shown side's queries fire.
  return isNewStatisticsEnabled ? <HealthProgramStatistics /> : <LegacyStatistics />
}

const HealthProgramStatistics = () => {
  const { t } = useTranslation()
  const {
    data: healthProgramStatistics,
    isLoading: isHealthProgramLoading,
    isError: isHealthProgramError,
  } = useGetHealthProgramStatistics()
  const {
    data: screeningInsights,
    isLoading: isScreeningInsightsLoading,
    isError: isScreeningInsightsError,
  } = useGetScreeningInsights()
  const { data: testimonials } = useGetTestimonials()
  const [isExporting, setIsExporting] = useState(false)

  if (isScreeningInsightsLoading || isHealthProgramLoading) return <LoadingSpinner color={theme.color.plum} size={60} />
  if (isScreeningInsightsError || isHealthProgramError)
    return <ComponentLayout>{t('common.errors.somethingWentWrong')}</ComponentLayout>

  return (
    <ComponentLayout paddingBottom={theme.spacing.jumbo}>
      <Eyebrow>{t('admin.statistics.title')}</Eyebrow>
      <SummarySection
        metrics={getSummaryMetrics(screeningInsights)}
        action={
          <ExportReportButton
            hasInsufficientData={hasInsufficientReportData(screeningInsights, healthProgramStatistics)}
            onExport={() => setIsExporting(true)}
          />
        }
      />
      <EducationalSection />
      {(isScreeningInsightsError || isHealthProgramError) && (
        <BodySmall>{t('common.errors.somethingWentWrong')}</BodySmall>
      )}
      <OrganisationNeedsSection insights={screeningInsights} />
      <HealthProgramSection statistics={healthProgramStatistics} />
      <TestimonialsSection testimonials={testimonials?.testimonials ?? []} />
      <CompetenceSection statistics={healthProgramStatistics} />
      {isExporting && (
        <PrintReport
          insights={screeningInsights}
          healthProgramStatistics={healthProgramStatistics}
          testimonials={testimonials?.testimonials ?? []}
          onDone={() => setIsExporting(false)}
        />
      )}
    </ComponentLayout>
  )
}

const Eyebrow = styled(BodyTinyMedium)`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${theme.color.blueDark};
  margin-bottom: ${theme.spacing.small}px;
`
